import {Component, Input, OnInit, QueryList, ViewChildren} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {VendorsService} from '../../services/vendors/vendors.service';
import {CryptoService} from '../../services/crypto/crypto.service';
import {Router} from '@angular/router';
import {ToastComponent} from '../toast/toast.component';
import {Location} from '@angular/common';
import {UsersInsuranceService} from '../../services/usersinsurance/users.insurance.service';
import {UsersService} from '../../services/users/users.service';
import {UtilsService} from '../../services/utils/utils.service';
import {RewardsService} from '../../services/rewards/rewards.service';
import {PurchaseService} from '../../services/purchase/purchase.service';
import {IonSelect} from '@ionic/angular';

interface Values {
  [field: string]: string | number;
}

@Component({
  selector: 'app-groupproducts',
  templateUrl: './groupproducts.component.html',
  styleUrls: ['./groupproducts.component.scss'],
})
export class GroupproductsComponent implements OnInit {

  @Input() params;

  @ViewChildren('choices') choiceSelects: QueryList<IonSelect>;

  protected products;
  protected compareCount = 0;
  protected comparePlans = [];
  protected compareAttributes = [];
  protected compareMode = false;
  protected noHeaderValues = false;
  private options = {};
  private basevalues = {};
  private spouseoptions = {};
  private spousebasevalues = {};
  private selectedOptionLabels;
  private basePlan = { tier: '', plan: '', cost: null };
  private selectedPlan?: string;

  expandedSections: { [key: number]: boolean } = {};

  constructor(
      private domSanitizer: DomSanitizer,
      private vendorsService: VendorsService,
      private cryptoService: CryptoService,
      private router: Router,
      private toastComponent: ToastComponent,
      private location: Location,
      private utilsService: UtilsService,
      private rewardsService: RewardsService,
      private usersInsuranceService: UsersInsuranceService,
      private usersService: UsersService,
      private purchaseService: PurchaseService
  ) { }

  async ngOnInit() {
    // TODO: Pull tier and plan from original usersVendorProducts entry? disabled for now and setting defaults below
    // if (this.params?.arguments?.employerEnrolled || this.params?.arguments?.upgradeFromEmployerEnrolled) {
    //   this.basePlan.tier = this.params?.arguments?.tier;
    //   this.basePlan.plan = this.params?.arguments?.plan;
    // }
    return this.vendorsService.getVendorGroupProductForUser(this.params.arguments.vendorProductId).then(async result => {
      for (const product of result) {
        if (product.vendorProductIds.includes(this.params.arguments.vendorProductId)) {
          this.vendorsService.getProductChoices(product.id).then(productChoices => {
            product.choices = productChoices.choices;
            product.restrictions = productChoices.restrictions?.options;
            product.metadata.logo = this.domSanitizer.bypassSecurityTrustUrl(product.metadata.logo);
            product.choices.forEach(choice => {
              choice.currentValue = choice.selectoptions[0].label;
              choice.appliesTo = choice.selectoptions[0].appliesTo;
              this.options[choice.name] = choice.selectoptions[0].values;
              this.basevalues[choice.name] = choice.selectoptions[0].basevalues;
              this.spouseoptions[choice.name] = choice.selectoptions[0].spousevalues;
              this.spousebasevalues[choice.name] = choice.selectoptions[0].spousebasevalues;
            });
            // If employer enrolled or upgrading from employer enrolled, set the base plan values so the prices
            // all have that original cost subtracted in the `updateValues` call
            if ((this.params?.arguments?.employerEnrolled || this.params?.arguments?.upgradeFromEmployerEnrolled)
              && !this.basePlan.plan) {
              this.basePlan.tier = 'EMPL';
              this.basePlan.plan = product.choices[0].currentValue;
            }
            this.setSelectedOptionLabel(product);
            this.updateValues(product);
            this.buildComparisonGrid(product);
          }).catch(error => {
            console.log('ERROR*******', error);
            this.toastComponent.presentToast(error.error);
          });
          product.vendorProductId = this.params.arguments.vendorProductId;
          this.products = [product];
          const dependents = await this.usersInsuranceService.getInsuranceDependents(this.usersService.getCurrentUserId(), this.params.arguments.vendorProductId);
          this.products[0].plans.forEach(plan => {
            plan.hasSpouse = dependents.some(dependent => ['s', 'partner'].includes(dependent.relationship.toLowerCase()));
            if (plan.spouseAgeRequired && !plan.hasSpouse) {
              plan.attributes = [{header: '', contents: 'Dependent spouse age required', footer: ''}];
            }
          });
        }
      }
    }).catch(error => {
      this.toastComponent.presentToast(`${error.error.error || error.error}. Please check your profile, but you may not be eligible for this product.`);
      this.router.navigate(['EditProfile/profile'], {replaceUrl: true});
    });
  }

  private buildComparisonGrid(product) {
    let options;
    const choice = product.choices[0];
    if (product.choices?.length > 0) {
      options = choice.selectoptions;
    }
    if (options) {
      this.noHeaderValues = choice.comparison.noPlanValues || choice.comparison.noCompare;
      const topHeader = choice.comparison.noCompare ? 'Plan Benefit Highlights' : 'Comparison Benefit Highlights';
      product.comparison = {
        // TODO: THIS ONLY WORKS FOR A SINGLE PLAN CHOICE DROPDOWN
        label: choice.label,
        colHeaders: choice.comparison.noPlanValues || choice.comparison.noCompare ? [topHeader] : [topHeader, ...options.map(option => option.label)],
        rows: choice.comparison?.rows.map(row => choice.comparison.noPlanValues ? [row.label, row.field || '_'] : [row.label, ...choice.selectoptions.map(option => row.field ? (option.values.EMPL[row.field] || row.field) : '_')]) || []
      };
    }
  }

  allCellsEqual(cells: any[]): boolean {
    return cells.length > 0 && cells.every(item => item === cells[0]);
  }

  private evaluateWithContext(code, context) {
    try {
      return Function(...Object.keys(context), `return ${code};`)(...Object.values(context));
    } catch (error) {
      return `Eval Error: ${error}`;
    }
  }

  private getCurrentOptions(plan, spouse = false) {
    const optionsForTier = {};
    const entries = spouse ? this.spouseoptions : this.options;
    Object.entries(entries || {}).forEach(([key, value]) => {
      Object.entries(value || {}).forEach(([key2, value2]) => {
        if (key2 === plan.tier) {
          optionsForTier[key] = value[key2];
        }
      });
    });
    return optionsForTier;
  }

  private evaluateFunctionString(input: string, context: any): string {
    const functionRegex = /__([a-zA-Z0-9]+)\(([^()]*)\)/g;

    let current = input;

    // Keep evaluating until there are no more function calls
    while (functionRegex.test(current)) {
      current = current.replace(functionRegex, (match, functionName, rawArgs) => {
        const evaluatedArgs = this.evaluateFunctionString(rawArgs, context); // recurse

        let result: any;
        switch (functionName) {
          case 'lookup':
            result = this.evaluateWithContext(evaluatedArgs, context);
            break;
          case 'currency':
            result = (eval(evaluatedArgs).toFixed(2));
            break;
          default:
            result = match; // pass through unrecognized functions
        }

        return String(result); // safe string coercion
      });
    }

    return current;
  }

  private updateValues(product) {
    const currentPlanChoice = product.choices.find((productChoice) => productChoice.currentValue === this.basePlan.plan);
    product.plans.forEach(plan => {
      plan.product = product;
      plan.isEmployerEnrolled = false;

      plan.attributes.forEach(attribute => {
        const original = attribute.savedcontents || attribute.contents;

        let updated = this.evaluateFunctionString(original, {
          option: this.getCurrentOptions(plan),
          basevalue: this.basevalues,
          spousebasevalue: this.spousebasevalues,
          spouseoption: this.getCurrentOptions(plan, true)
        });

        if (currentPlanChoice) {
          if (plan.tier === this.basePlan.tier) {
            plan.isEmployerEnrolled = true;
            const currencyString = updated[0];
            this.basePlan.cost = parseFloat(updated.slice(1));
            updated = `${currencyString}0`;
          }
        }
        // We only subtract the base plan cost if it is not the base plan (!plan.isEmployerEnrolled)
        if (this.basePlan.cost && !plan.isEmployerEnrolled) {
          const currencyString = updated[0];
          const newUpdatedCost = (parseFloat(updated.slice(1)) - this.basePlan.cost).toFixed(2);
          updated = `${currencyString}${newUpdatedCost}`;
        }

        if (original !== updated) {
          if (!attribute.savedcontents) {
            attribute.savedcontents = original;
          }
          attribute.contents = updated;
        }
      });

      plan.attributes = plan.attributes.slice(); // trigger change detection
      plan.show = true;
    });

    let matchedProduct = this.products.find(thisProduct => thisProduct.id === product.id);
    if (matchedProduct) {
      matchedProduct = product;
    }
  }

  private parseFunctionString(input) {
    const regex = /(.*)__([A-Z,a-z,0-9]+)\((.*)\)(.*)$/;
    const match = input.match(regex);
    if (match) {
      const prefix = match[1];
      const functionName = match[2]; // Extracts FUNCTIONNAME
      const args = match[3];        // Extracts ARGS
      const postfix = match[4];
      return { prefix, postfix, functionName, args };
    } else {
      return null;
    }
  }

  getTier(plan) {
    const tierNames = {
      EMPL: 'Member Only',
      SPSE: 'Member plus Spouse',
      DPDT: 'Member plus Family',
      SPF: 'Member plus Children'
    };
    return tierNames[plan.tier];
  }

  sanitizeHtml(html) {
    return this.domSanitizer.bypassSecurityTrustHtml(html);
  }

  showDocument(url, embedPdf) {
    this.rewardsService.gotoUrl(url, embedPdf);
  }

  compareBoxClicked(event, productPlan) {
    if (event.detail.checked) {
      this.compareCount++;
      productPlan.compareSelected = true;
    } else {
      this.compareCount--;
      productPlan.compareSelected = false;
    }
  }

  select(plan, product) {
   return this.vendorsService.getVendorProduct(product.vendorProductId).then(vendorProduct => {
      this.utilsService.dependentsMatchPlan(plan, product).then(match => {
        if (match) {
            return this.vendorsService.getVendorGroupPlan(plan.id).then(async planDetails => {
              const doQuestions = planDetails.questions && planDetails.questions.length > 0;
              const planName = `${vendorProduct.metadata?.displayName
                ? vendorProduct.metadata?.displayName
                : product.metadata.name} ${this.getTier(plan)}`;
              const onwardData = this.cryptoService.encodeJSON({
                doQuestions,
                action: this.params,
                tier: plan.tier,
                plan: this.selectedPlan,
                profileNotRequired: plan.profileNotRequired,
                detailsUrl: product.metadata.detailsUrl,
                planId: plan.id,
                options: this.getCurrentOptions(plan),
                planName,
                amount: parseFloat(plan.attributes[0].contents.replace(/[\$,]/g, '')),
                vendorGroupProductId: plan.product.id,
                form: {},
                purchaseInfo: plan.attributes.filter(attribute => attribute.showAtPurchase),
                purchaseInfoStyles: product.metadata.styles,
                selectedOptionLabels: this.selectedOptionLabels,
              });
              RewardsService.enrolling = true;
              const enrollStatus = await this.vendorsService.enrollProduct(vendorProduct.vendorSlug, vendorProduct.productSlug, {arguments: Object.assign({}, {action: this.params}, this.params.arguments, {testOnly: true})});
              if (enrollStatus.errors && enrollStatus.errors.length > 0) {
                this.purchaseService.presentMissingInfoMessage(enrollStatus.errors);
              } else {
                vendorProduct.metadata.selectProductFirst = false;
              }
              if (vendorProduct.metadata.selectProductFirst) {
                // now go to profile
                this.router.navigate(['/EditProfile/profile/group', onwardData]);
              } else {
                this.router.navigate([doQuestions ? 'group-product-questions' : 'group-product-enroll', onwardData]);
              }
            }).catch(error => {
              this.toastComponent.presentToast(error.error);
            });
        } else {
          this.router.navigate([ '/EditProfile/profile' ]);
          // this.location.back();
        }
      });
   });
  }

  compare(thisProduct) {
    if (thisProduct.comparison) {
      thisProduct.showComparison = !thisProduct.showComparison;
      return;
    }
    this.compareMode = !this.compareMode;
    this.comparePlans = [];
    this.compareAttributes = [];
    this.products.forEach(product => {
      product.plans.forEach(plan => {
        if (plan.compareSelected) {
          this.comparePlans.push(plan);
          plan.compare.forEach(comparison => {
            if (!this.compareAttributes.find(attribute => attribute === comparison.name)) {
              this.compareAttributes.push(comparison.name);
            }
          });
        }
      });
    });
    if (this.compareMode && this.compareAttributes.length === 0) {
      this.compareMode = false;
      this.toastComponent.presentToast('No attributes available to compare');
    }
  }

  expandTemplate(product, template: string): string {
    const func = this.parseFunctionString(template);
    if (func) {
      switch (func.functionName) {
        case 'lookup':
          return this.expandTemplate(product, func.prefix + this.evaluateWithContext(func.args, {option: this.getCurrentOptions(product.plans[0])}) + func.postfix);
      }
    }
    return template;
  }

  getComparisonValue(plan, compareAttribute) {
    const compareItem = plan.compare.find(comparison => comparison.name === compareAttribute);
    if (compareItem) {
      const func = this.parseFunctionString(compareItem.value);
      if (func) {
        switch (func.functionName) {
          case 'lookup':
            return func.prefix + this.evaluateWithContext(func.args, {option: this.getCurrentOptions(plan)}) + func.postfix;
        }
      }
    }
    return '';
  }

  private setSelectedOptionLabel(product) {
    const currentChoices = product.choices.map(choice => ({
      appliesTo: choice.appliesTo,
      label: `${choice.label} ${choice.currentValue}`
    }));
    console.log('CURRENTCHOICS', currentChoices);
    const filters = ['primary', 'spouse', 'dependents', 'beneficiaries'] as const;
    this.selectedOptionLabels = filters.reduce((acc, filter) => {
      acc[filter] = currentChoices
        .filter(choice => choice.appliesTo === filter)
        .map(choice => choice.label)
        .join(', ');
      return acc;
    }, {} as Record<typeof filters[number], string>);
  }

  private coerce(value: any): number {
    if (value == null) {
      return NaN;
    }
    // If it's already a number, return as-is
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      let str = value.trim();
      // Handle currency (starts with $)
      if (str.startsWith('$')) {
        // Remove $ and commas
        str = str.replace(/\$/g, '').replace(/,/g, '');
      }
      const num = parseFloat(str);
      return isNaN(num) ? NaN : num;
    }
    // Fallback: try generic conversion
    const num = Number(value);
    return isNaN(num) ? NaN : num;
  }

  validateRule(values: Values, restriction: any): { valid: boolean; message?: string } {
    const rule = restriction.rule;
    const target = restriction.target;

    console.log('validaterule', rule, target, values);
    switch (rule.type) {
      case 'maxRelative':
        const okMax = values[rule.field] !== undefined &&
          this.coerce(values[target]) <= this.coerce(values[rule.field]) * rule.ratio;
        return { valid: okMax, message: okMax ? undefined : restriction.message };

      case 'minOffset':
        const okMin = values[rule.field] !== undefined &&
          this.coerce(values[target]) >= this.coerce(values[rule.field]) + rule.offset;
        return { valid: okMin, message: okMin ? undefined : restriction.message };

      case 'notEqualCombo':
        const okCombo = !rule.disallowed.some(
          (pair: any[]) => rule.fields.every((f, i) => values[f] === pair[i])
        );
        return { valid: okCombo, message: okCombo ? undefined : restriction.message };

      default:
        throw new Error(`Unknown rule type: ${rule.type}`);
    }
  }

  isValidField(
    field: string,
    value: any,
    values: Values,
    restrictions?: any[]
  ): { valid: boolean; message?: string } {
    const updatedValues = { ...values, [field]: value };
    if (restrictions && restrictions.length > 0) {
      for (const r of restrictions.filter(r => r.target === field || r.dependsOn.includes(field))) {
        const result = this.validateRule(updatedValues, r);
        if (!result.valid) {
          return result; // includes message
        }
      }
    }
    return { valid: true };
  }


  optionChange(event, product, name) {
    const choice = product.choices.find(product => product.name === name);

    // build values object { fieldName: currentValue }
    const values = {};
    for (const c of product.choices) {
      values[c.name] = c.currentValue;
    }

    const result = this.isValidField(name, event.detail.value, values, product.restrictions);

    if (!result.valid) {
      this.toastComponent.presentToast(result.message || 'Invalid selection');
      // reset to previous value
      this.choiceSelects.get(product.choices.indexOf(choice)).value = choice.currentValue;
      return;
    }

    // valid → update
    choice.currentValue = event.detail.value;
    this.selectedPlan = choice.currentValue;
    this.options[name] = choice?.selectoptions.find(option => option.label === event.detail.value)?.values;
    this.spouseoptions[name] = choice?.selectoptions.find(option => option.label === event.detail.value)?.spousevalues;
    this.setSelectedOptionLabel(product);
    this.updateValues(product);
  }

  toggleSection(index: number): void {
    this.expandedSections[index] = !this.expandedSections[index];
  }

  isRowVisible(product, index: number): boolean {
    // Find the closest preceding section header
    let sectionIndex = -1;
    for (let i = index; i >= 0; i--) {
      if (product.comparison.rows[i][1] === '_') {
        sectionIndex = i;
        break;
      }
    }
    // If no section header is found, assume the row is visible
    if (sectionIndex === -1) {
      return true;
    }
    // Check if the section is expanded
    return this.expandedSections[sectionIndex];
  }

}
